import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationContext";
import API_URL from "../config";

export default function Socios() {
  const [periodos, setPeriodos] = useState([]);
  const [periodSelecionado, setPeriodSelecionado] = useState("");
  const [dados, setDados] = useState([]);
  const [dataOriginal, setDataOriginal] = useState([]);
  const [periodosAnteriores, setPeriodosAnteriores] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [filtroNome, setFiltroNome] = useState("");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { token } = useAuth();
  const { notify } = useNotification();

  // Carregar períodos ao montar o componente
  useEffect(() => {
    carregarPeriodos();
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const ordenarPeriodosDesc = (periodos) => {
    return [...periodos].sort((a, b) => {
      const chaveA = `${a.substring(2)}${a.substring(0, 2)}`;
      const chaveB = `${b.substring(2)}${b.substring(0, 2)}`;
      return chaveB.localeCompare(chaveA);
    });
  };

  const carregarPeriodos = async () => {
    try {
      const res = await fetch(`${API_URL}/api/relatorio/socios/periodos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Erro ao carregar períodos");
      const data = await res.json();
      const periodosOrdenados = ordenarPeriodosDesc(data);
      setPeriodos(periodosOrdenados);
      if (periodosOrdenados.length > 0) {
        setPeriodSelecionado(periodosOrdenados[0]);
        carregarDados(periodosOrdenados[0]);
      }
    } catch (err) {
      notify.error("Erro ao carregar períodos: " + err.message);
    }
  };

  const carregarDados = async (periodo) => {
    if (!periodo) return;
    setCarregando(true);
    try {
      const res = await fetch(`${API_URL}/api/relatorio/socios/dados/${periodo}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Dados não encontrados");
      const json = await res.json();
      const lista = json.dados ?? json; // compatibilidade
      setPeriodosAnteriores(json.periodosAnteriores ?? []);
      setDataOriginal(lista);
      setDados(lista);
      setFiltroNome("");
      notify.success(`${lista.length} sócio(s) carregado(s) com sucesso!`);
    } catch (err) {
      notify.error("Erro ao carregar dados: " + err.message);
    } finally {
      setCarregando(false);
    }
  };

  const handleChangePeriodo = (novoPeriodo) => {
    setPeriodSelecionado(novoPeriodo);
    carregarDados(novoPeriodo);
  };

  const handleFiltroNome = (valor) => {
    setFiltroNome(valor);
    if (valor.trim() === "") {
      setDados(dataOriginal);
    } else {
      const filtrado = dataOriginal.filter((item) =>
        item.nome.toLowerCase().includes(valor.toLowerCase()) ||
        item.matricula.toString().includes(valor)
      );
      setDados(filtrado);
    }
  };

  const formatarValor = (valor) => {
    const numero = parseFloat(String(valor).replace(/\s/g, "").replace(",", ".")) || 0;
    return numero.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatarPeriodo = (periodo) => {
    if (!periodo || periodo.length < 6) return periodo;
    const mes = parseInt(periodo.substring(0, 2)) - 1;
    const ano = periodo.substring(2);
    const meses = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
                   "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
    return `${meses[mes]} ${ano}`;
  };

  const totalValor = dados.reduce((acc, item) => {
    const valor = parseFloat(String(item.valor).replace(/\s/g, "").replace(",", ".")) || 0;
    return acc + valor;
  }, 0);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div className="w-full max-w-6xl mx-auto px-4 py-6">
      {/* Cabeçalho */}
      <div className="border-b-2 border-blue-600 pb-3 mb-6">
        <p className="text-xs font-semibold text-blue-400 uppercase tracking-widest mb-1">SINDIÁGUA-PB</p>
        <h1 className="text-2xl font-bold text-gray-800">Sócios</h1>
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Período</label>
          <div className="relative">
            <select
              value={periodSelecionado}
              onChange={(e) => handleChangePeriodo(e.target.value)}
              className="w-full h-10 pl-3 pr-9 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition appearance-none cursor-pointer"
            >
              {periodos.map((p) => (
                <option key={p} value={p}>{formatarPeriodo(p)}</option>
              ))}
            </select>
            <svg className="pointer-events-none absolute right-3 top-3 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Buscar por Nome ou Matrícula</label>
          <div className="relative">
            <input
              type="text"
              placeholder="Digite o nome ou matrícula..."
              value={filtroNome}
              onChange={(e) => handleFiltroNome(e.target.value)}
              className="w-full h-10 pl-9 pr-4 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
            />
            <svg className="pointer-events-none absolute left-3 top-3 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8"/><path strokeLinecap="round" d="M21 21l-4.35-4.35"/>
            </svg>
            {filtroNome && (
              <button onClick={() => handleFiltroNome("")} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 text-sm">✕</button>
            )}
          </div>
        </div>
      </div>

      {/* Dados da Tabela */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
            Sócios Registrados
          </h2>
          <span className="text-xs bg-blue-50 text-blue-600 font-semibold px-2 py-1 rounded-full">
            {dados.length} registro(s)
          </span>
        </div>

        {carregando ? (
          <div className="px-6 py-12 text-center text-gray-500">
            <div className="inline-block animate-spin">⟳</div>
            <p className="mt-2">Carregando dados...</p>
          </div>
        ) : dados.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            <p>Nenhum sócio encontrado para este período</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Matrícula</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Nome</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                      {formatarPeriodo(periodSelecionado)}
                    </th>
                    {periodosAnteriores.map((p) => (
                      <th key={p} className="text-right px-4 py-3 text-xs font-semibold text-gray-300 uppercase tracking-wide whitespace-nowrap">
                        {formatarPeriodo(p)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dados.map((item, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-blue-50 transition-colors">
                      <td className="px-6 py-3 font-mono text-gray-500 text-sm">{item.matricula}</td>
                      <td className="px-6 py-3 font-medium text-gray-700 text-sm">{item.nome}</td>
                      <td className="px-4 py-3 text-right text-gray-700 text-sm font-semibold">
                        R$ {formatarValor(item.valor)}
                      </td>
                      {(item.historico ?? []).map((h, j) => (
                        <td key={j} className="px-4 py-3 text-right text-sm text-gray-400">
                          {h.valor !== null ? `R$ ${formatarValor(h.valor)}` : <span className="text-gray-300">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Rodapé com Total */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">
                  Total de {dados.length} sócios
                </span>
                <span className="text-lg font-bold text-blue-600">
                  R$ {formatarValor(totalValor)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Botão Scroll to Top */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
          title="Voltar ao topo"
        >
          ↑
        </button>
      )}
    </div>
  );
}
